import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';

import ApiService from '@app/Services/ApiService';
import { PersonAirbusTypeModel } from '@app/Models/WebApiModels';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';

import ReactSelect, { ReactSelectValue, ReactSelectSize } from '@app/Components/ReactSelect';
import { shallowEqual } from 'shallow-equal-object';
import { applyDecorators } from "@app/Helpers/Decorator";

type AirbusTypePickerProps = {
    clearable?: boolean;
    multi?: boolean;
    value?: string | null;
    selectedCrewFunction?: string | null;
    onSelect?: (value: string | null) => void;
    selectSize?: ReactSelectSize;
};

@observer
export default class AirbusTypePicker extends React.Component<AirbusTypePickerProps> {
    private _store: AirbusTypePickerStore;

    constructor(props: AirbusTypePickerProps) {
        super(props);
        applyDecorators(this);
        this._store = new AirbusTypePickerStore();
    }

    componentDidMount(): void {
        void this._store.loadData(this.props.selectedCrewFunction);
    }

    componentDidUpdate(prevProps: AirbusTypePickerProps): void {
        if (!shallowEqual(prevProps, this.props)) {
            this._store.loadData(this.props.selectedCrewFunction);
        }
    }

    render() {
        const { clearable, selectSize, multi, value } = this.props;
        const { options } = this._store;
        return <ReactSelect placeholder="All" options={options} multi={multi} value={value} isClearable={clearable} selectSize={selectSize} onSelect={this._onChangeHandler} />;
    }

    @action.bound
    private _onChangeHandler(value: ReactSelectValue) {
        this.props.onSelect && this.props.onSelect(value);
    }
}

class AirbusTypePickerStore {
    @observable airbusTypes: PersonAirbusTypeModel[] = [];
    private _currentFunctionGroup: string | null = null;

    constructor() {
        applyDecorators(this);
    }

    @action
    public loadData(selectedCrewFunction?: string | null) {
        if (this._currentFunctionGroup !== selectedCrewFunction) {
            this._currentFunctionGroup = selectedCrewFunction || null;
            void this.loadAirbusTypesFilters();
        }
    }

    @action.bound
    public async loadAirbusTypesFilters() {
        if (this._currentFunctionGroup === FunctionGroupIds.Fcm) {
            const [{ data: airbusTypesCabin }, { data: airbusTypesCockpit }] = await Promise.all([
                ApiService.getData(ApiUrls.AirbusTypesUrl, { functionGroupId: FunctionGroupIds.Cabin }),
                ApiService.getData(ApiUrls.AirbusTypesUrl, { functionGroupId: FunctionGroupIds.Cockpit })
            ]);
            this.airbusTypes = [...airbusTypesCabin, ...airbusTypesCockpit].filter((val, i, self) => self.findIndex(t => t.id === val.id) === i);
            return;
        }

        const params = {
            functionGroupId: this._currentFunctionGroup
        };
        const airbusTypesResponse = await ApiService.getData(ApiUrls.AirbusTypesUrl, params);
        this.airbusTypes = airbusTypesResponse.data || [];
    }

    @computed
    public get options() {
        return this.airbusTypes.map((type) => {
            return { value: type.id, label: type.name || '', isHidden: !type.isVisible };
        });
    }
}
